import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Savings,
} from '@mui/icons-material';
import { Transaction, Category, Budget } from '../types';
import { formatCurrency } from '../utils/dateUtils';
import { MobileCard, MobilePageHeader } from './MobileOptimizations';
import MobileTransactionList from './MobileTransactionList';

interface MobileDashboardProps {
  transactions: Transaction[];
  categories: Category[];
  budgets: Budget[];
}

const MobileDashboard: React.FC<MobileDashboardProps> = ({
  transactions,
  categories,
  budgets,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Calculs pour le mois courant
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const currentMonthTransactions = transactions.filter(t => {
    const transactionDate = new Date(t.date);
    return transactionDate.getMonth() === currentMonth && 
           transactionDate.getFullYear() === currentYear;
  });

  const totalIncome = currentMonthTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpenses = currentMonthTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const balance = totalIncome - totalExpenses;
  const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0;

  // Calculs des budgets
  const budgetProgress = budgets.map(budget => {
    const spent = currentMonthTransactions
      .filter(t => t.type === 'expense' && t.category === budget.category)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const percentage = budget.allocated > 0 ? (spent / budget.allocated) * 100 : 0;
    
    return {
      ...budget,
      spent,
      remaining: budget.allocated - spent,
      percentage: Math.min(percentage, 100),
      isOverBudget: spent > budget.allocated,
    };
  });

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    subtitle 
  }: { 
    title: string; 
    value: string; 
    icon: React.ReactNode; 
    color: string;
    subtitle?: string;
  }) => (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${color}15, ${color}05)`,
        border: `1px solid ${color}30`,
        height: '100%',
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: `${color}20`,
              color: color,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{ fontWeight: 'bold', color: color }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <MobilePageHeader
        title="Dashboard"
        subtitle={new Date().toLocaleDateString('fr-FR', { 
          month: 'long', 
          year: 'numeric' 
        })}
      />

      {/* Statistiques principales */}
      <Box 
        sx={{ 
          mb: 3,
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? 2 : 3,
        }}
      >
        <StatCard
          title="Revenus"
          value={formatCurrency(totalIncome)}
          icon={<TrendingUp />}
          color={theme.palette.success.main}
        />
        <StatCard
          title="Dépenses"
          value={formatCurrency(totalExpenses)}
          icon={<TrendingDown />}
          color={theme.palette.error.main}
        />
        <StatCard
          title="Solde"
          value={formatCurrency(balance)}
          icon={<AccountBalance />}
          color={balance >= 0 ? theme.palette.success.main : theme.palette.error.main}
        />
        <StatCard
          title="Épargne"
          value={`${savingsRate.toFixed(1)}%`}
          icon={<Savings />}
          color={theme.palette.info.main}
          subtitle={formatCurrency(balance)}
        />
      </Box>

      {/* Progression des budgets */}
      {budgetProgress.length > 0 && (
        <MobileCard title="Budgets du mois">
          <Stack spacing={2}>
            {budgetProgress.slice(0, isMobile ? 3 : 5).map((budget) => (
              <Box key={budget.category}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                    {budget.category}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant={isMobile ? 'body2' : 'body1'}>
                      {formatCurrency(budget.spent)} / {formatCurrency(budget.allocated)}
                    </Typography>
                    {budget.isOverBudget && (
                      <Chip
                        label="Dépassé"
                        size="small"
                        color="error"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={budget.percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      bgcolor: budget.isOverBudget 
                        ? theme.palette.error.main 
                        : budget.percentage > 80 
                          ? theme.palette.warning.main 
                          : theme.palette.success.main,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {budget.percentage.toFixed(1)}% utilisé
                </Typography>
              </Box>
            ))}
          </Stack>
        </MobileCard>
      )}

      {/* Transactions récentes */}
      <MobileCard title="Transactions récentes">
        <MobileTransactionList
          transactions={currentMonthTransactions.slice(0, isMobile ? 5 : 8)}
          categories={categories}
          maxItems={isMobile ? 5 : 8}
        />
      </MobileCard>
    </Box>
  );
};

export default MobileDashboard;
